import { HttpEvent          ,
         HttpHandler        ,
         HttpInterceptor    ,
         HttpRequest        } from '@angular/common/http';
import { inject             ,
         Injectable         } from '@angular/core';
import { MsalBroadcastService,
         MsalService        } from '@azure/msal-angular';
import { AccountInfo        ,
         InteractionStatus  } from '@azure/msal-browser';
import { Observable         ,
         of                 ,
         catchError         ,
         filter             ,
         map                ,
         switchMap          ,
         take               ,
         tap                } from 'rxjs';
import { environment        } from 'environments/environment';

/**
 * Attaches the signed-in user's Entra ID token to calls made to our own API, which
 * validates it on every request.
 *
 * Only our API gets the header. Calls to Microsoft Graph are handled by MsalInterceptor,
 * which attaches an access token scoped to Graph - overwriting it here would break them.
 */
@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  private authService       : MsalService           = inject(MsalService);
  private broadcastService  : MsalBroadcastService  = inject(MsalBroadcastService);

  public intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    if (!req.url.startsWith(environment.serviceUrls.application)) {
      return next.handle(req);
    }

    return this.token().pipe(
      switchMap(token => next.handle(token ? this.authorize(req, token) : req))
    );
  }

  private authorize(req: HttpRequest<any>, token: string): HttpRequest<any> {

    return req.clone({
      setHeaders: {
        'Authorization': `Bearer ${token}`,
      },
    });
  }

  /**
   * The token to send, or null when the user is not signed in yet.
   *
   * Two things make this asynchronous. MSAL only finishes reading the sign-in response
   * after the app has started, so a call fired from a component's ngOnInit would
   * otherwise go out before there is any account to read - waiting on inProgress$ holds
   * the request until MSAL has settled. And the cached token expires after about an
   * hour, so it is acquired rather than read: acquireTokenSilent hands back a cached
   * token while it is valid and quietly renews it once it is not.
   */
  private token(): Observable<string | null> {

    return this.broadcastService.inProgress$.pipe(
      filter(status => status === InteractionStatus.None),
      take(1),
      switchMap(() => {

        const account = this.account();

        if (!account) {
          return of(null);
        }

        return this.authService
          .acquireTokenSilent({ scopes: environment.apiConfig.scopes, account })
          .pipe(
            map(result => result.idToken),
            catchError(error => {
              // Renewal needs the user present. Let the call go out bare so the API
              // answers 401 and MsalGuard takes them through sign-in on the next route.
              console.error('Could not acquire a token for the API: ', error);
              return of(null);
            })
          );
      })
    );
  }

  /**
   * The active account, falling back to the first cached one. MsalGuard only marks an
   * account active on the popup flow, never on the redirect flow this app uses.
   */
  private account(): AccountInfo | null {

    return this.authService.instance.getActiveAccount() ?? this.authService.instance.getAllAccounts()[0] ?? null;
  }
}


@Injectable()
export class ForbiddenInterceptor implements HttpInterceptor {

  private authService: MsalService = inject(MsalService);

  public intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(tap(x => {
    }))
  }
}
